import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"

import { useUpdateGoodsReceiptPalletRows } from "../../../hooks"
import type { SoftReceivedPurchaseOrdersFormValues } from "../../../schemas"
import { createSoftReceivedPurchaseOrdersFormSchema } from "../../../schemas"
import { getSoftReceivedPurchaseOrdersFormDefaultValues } from "../../../utils"
import { serializeSoftReceivedPurchaseOrderForm } from "../utils"

import type { GoodsReceipt, GoodsReceiptPalletRow } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"
import type { ActionOutcome } from "@/types/common"
import { runWithGuardRef } from "@/utils/forms"

interface UseSoftReceivedPurchaseOrdersFormParams {
  goodsReceipt: GoodsReceipt | null
  goodsReceiptPalletRows: GoodsReceiptPalletRow[]
  onSuccess: () => void
}

interface UseSoftReceivedPurchaseOrdersFormReturn {
  methods: ReturnType<typeof useForm<SoftReceivedPurchaseOrdersFormValues>>
  canSubmit: boolean
  isCancelDialogOpen: boolean
  isResettingRef: React.MutableRefObject<boolean>
  handleCancel: () => void
  handleCloseCancelConfirmation: () => void
  handleConfirmCancel: () => void
  handleSave: () => Promise<ActionOutcome<GoodsReceiptPalletRow[]>>
  handleReset: (formValues: SoftReceivedPurchaseOrdersFormValues) => void
}

export const EMPTY_DEFAULT_FORM_VALUES: SoftReceivedPurchaseOrdersFormValues = {
  goodsReceiptId: "",
  purchaseOrders: [],
}

export const useSoftReceivedPurchaseOrdersForm = ({
  goodsReceipt,
  goodsReceiptPalletRows,
  onSuccess,
}: UseSoftReceivedPurchaseOrdersFormParams): UseSoftReceivedPurchaseOrdersFormReturn => {
  const navigate = useNavigate()
  const { getBasicPathFor } = useAppView()
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const isResettingRef = useRef(false)
  const methods = useForm<SoftReceivedPurchaseOrdersFormValues>({
    defaultValues: EMPTY_DEFAULT_FORM_VALUES,
    resolver: zodResolver(createSoftReceivedPurchaseOrdersFormSchema(false)),
    mode: "onChange",
  })
  const {
    formState: { isDirty, isValid, isSubmitting },
    getValues,
    reset,
    trigger,
  } = methods
  const { update } = useUpdateGoodsReceiptPalletRows(onSuccess)
  const canSubmit = isDirty && isValid && !isSubmitting

  const handleReset = useCallback(
    (formValues: SoftReceivedPurchaseOrdersFormValues) => {
      runWithGuardRef(() => reset(formValues), isResettingRef)
    },
    [reset]
  )

  useEffect(() => {
    if (goodsReceipt) {
      const formValues = getSoftReceivedPurchaseOrdersFormDefaultValues(goodsReceipt, goodsReceiptPalletRows)
      handleReset(formValues)
    }
  }, [goodsReceipt, goodsReceiptPalletRows, handleReset])

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      const softReceivedPath = `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received`
      navigate({ to: softReceivedPath })
    }
  }

  const handleCloseCancelConfirmation = () => setIsCancelDialogOpen(false)

  const handleConfirmCancel = () => {
    handleReset(EMPTY_DEFAULT_FORM_VALUES)
    const softReceivedPath = `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received`
    navigate({ to: softReceivedPath })
  }

  const handleSave = useCallback(async (): Promise<ActionOutcome<GoodsReceiptPalletRow[]>> => {
    const isValid = await trigger()

    if (!isValid) {
      return { error: new Error("Please correct all validation errors before saving.") }
    }

    const formValues = getValues()
    const input = serializeSoftReceivedPurchaseOrderForm(formValues)

    return await update(input)
  }, [trigger, getValues, update])

  return {
    methods,
    canSubmit,
    isCancelDialogOpen,
    isResettingRef,
    handleCloseCancelConfirmation,
    handleConfirmCancel,
    handleCancel,
    handleSave,
    handleReset,
  }
}
